import React, { lazy, Suspense, type ComponentType } from 'react'
import { Button, MenuOverlay, useOverlay, Skeleton, ActionChip, Divider } from '@fs/zion-ui'
import type { FamilyMembers, Person } from '@fs/zion-tree-types'
import { useFeatureFlag } from '@fs/zion-flags'
import {
  getFSSearchUrl,
  getAncestryUrl,
  getFindMyPastUrl,
  getMyHeritageUrl,
  getMyHeritageLibraryUrl,
  getGeneanetUrl,
  getFilaeUrl,
  getFSTreesUrl,
  getFSFullTextUrl,
  getStoriedUrl,
} from '@fs/zion-search-partners'
import { usePermission } from '@fs/zion-permissions'
import { useTranslation } from 'react-i18next'
import { DocumentSmart, LogoFamilysearch, MenuSearch, PersonSearch } from '@fs/zion-icon'
import { useGroupsContext } from '@fs/zion-tree-groups'
import { useFocusFamilyMembersContext } from '../../contexts/FocusFamilyMembersContext'
import useFocusPerson from '../../../data-service/useFocusPerson'
import {
  AncestryIcon,
  FilaeIcon,
  FindmypastIcon,
  GoogleIcon,
  GeneanetIcon,
  MyHeritageIcon,
  StoriedIcon,
} from '../../cards/search-records-card/SearchPartnerIcons'

import type { SearchName } from '../../cards/search-records-card/SearchRecordsCard'

const SearchItem = lazy(() => import('../../cards/search-records-card/SearchItem'))
const BasicSearchItem = lazy(() => import('../../cards/search-records-card/BasicSearchItem'))
const GoogleSearch = lazy(() => import('../../cards/search-records-card/GoogleSearch'))

type SearchItemDef = {
  key?: string
  name: SearchName
  linkName?: string
  Icon: ComponentType
  getUrl: (person: Person, familyMembers: FamilyMembers) => string
}

type Props = {
  'data-testid'?: string
  /** Namespaces the menu's item test ids; see BasicSearchItem. */
  testIdPrefix?: string
}

export default function SearchRecordsMenuOverlay({
  'data-testid': dataTestId = 'banner-search-records-menu',
  testIdPrefix,
}: Props): JSX.Element | null {
  const { canAccessCET } = useGroupsContext()
  const developerMode = useFeatureFlag('treeWeb_developerMode').isOn
  const [t] = useTranslation()
  const [showFHCenterOnlyLinks] = usePermission('AncestryInstitutePermission')
  const showInstitutionLinks = showFHCenterOnlyLinks || developerMode
  const { focusPerson } = useFocusPerson()
  const { focusFamilyMembers } = useFocusFamilyMembersContext()
  const googleOverlay = useOverlay()

  if (!focusPerson || !focusFamilyMembers) {
    return (
      <Button Icon={MenuSearch} emphasis="medium" disabled data-testid={dataTestId}>
        {t('tree-person.search-records.label')}
      </Button>
    )
  }

  const familySearchItems: (SearchItemDef | false)[] = [
    {
      name: t('tree-person.fs-records.label'),
      key: 'FamilySearch Records',
      linkName: 'Search: FamilySearch',
      Icon: LogoFamilysearch,
      getUrl: getFSSearchUrl,
    },
    {
      name: t('tree-person.fs-full-text.label'),
      key: 'FamilySearch Full Text',
      linkName: 'Search: FamilySearch Full Text',
      Icon: DocumentSmart,
      getUrl: getFSFullTextUrl,
    },
    !!canAccessCET && {
      name: t('tree-person.fs-trees.label'),
      key: 'FamilySearch Trees',
      linkName: 'Search: FamilySearch Trees',
      Icon: PersonSearch,
      getUrl: (person: Person, familyMembers: FamilyMembers) =>
        getFSTreesUrl({
          person,
          familyMembers,
          searchCetTreesOnly: true,
          includeTreeRef: true,
        }),
    },
  ]

  const partnerSearchItems: (SearchItemDef | false)[] = [
    {
      name: 'Ancestry',
      Icon: AncestryIcon,
      getUrl: getAncestryUrl,
    },
    showInstitutionLinks && {
      name: 'Ancestry Institution',
      Icon: AncestryIcon,
      getUrl: (person: Person, familyMembers: FamilyMembers) => getAncestryUrl(person, familyMembers, true),
    },
    {
      name: 'MyHeritage',
      Icon: MyHeritageIcon,
      getUrl: getMyHeritageUrl,
    },
    showInstitutionLinks && {
      name: 'MyHeritage Library Edition',
      Icon: MyHeritageIcon,
      getUrl: getMyHeritageLibraryUrl,
    },
    {
      name: 'Findmypast',
      Icon: FindmypastIcon,
      getUrl: getFindMyPastUrl,
    },
    {
      name: 'Geneanet',
      Icon: GeneanetIcon,
      getUrl: getGeneanetUrl,
    },
    {
      name: 'Storied',
      Icon: StoriedIcon,
      getUrl: getStoriedUrl,
    },
    {
      name: 'Filae',
      Icon: FilaeIcon,
      getUrl: getFilaeUrl,
    },
  ]

  return (
    <>
      <MenuOverlay
        menuButton={
          <ActionChip
            Icon={MenuSearch}
            emphasis="high"
            data-testid={dataTestId}
            label={t('tree-person.search-records.label')}
            linkName="Person Header Search Records Menu"
          />
        }
        aria-label={t('tree-person.search-records.label')}
        inline={false}
      >
        <Suspense fallback={<Skeleton.ListItem dense hasIcon />}>
          {familySearchItems
            .filter((item) => !!item)
            .map((item) => (
              <BasicSearchItem
                {...item}
                to={item.getUrl(focusPerson, focusFamilyMembers)}
                target="_blank"
                external
                testIdPrefix={testIdPrefix}
              />
            ))}
        </Suspense>
        <Divider marginY="nano" />
        <Suspense fallback={<Skeleton.ListItem dense hasIcon />}>
          {partnerSearchItems
            .filter((item) => !!item)
            .map((item) => (
              <SearchItem
                key={item.name}
                {...item}
                url={item.getUrl(focusPerson, focusFamilyMembers)}
                testIdPrefix={testIdPrefix}
              />
            ))}
        </Suspense>

        {/* eslint-disable-next-line test-selectors/onClick -- NA */}
        <BasicSearchItem
          onClick={googleOverlay.handleClick}
          Icon={GoogleIcon}
          linkName="Search: Google Open"
          name="Google"
          testIdPrefix={testIdPrefix}
        />
      </MenuOverlay>

      <Suspense fallback={null}>
        {focusPerson && (googleOverlay.isOpen || googleOverlay.transitioning) && (
          <GoogleSearch focusPerson={focusPerson} focusFamilyMembers={focusFamilyMembers} overlay={googleOverlay} />
        )}
      </Suspense>
    </>
  )
}
